import React, { FC, useEffect, useState } from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  BrowserRouter,
} from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ThemeProvider } from 'styled-components'
import * as Sentry from '@sentry/react'

import store from '../../store'
import messagesEN from '../../i18n/en.json'
import I18NText from '../I18NText'
import {
  AppTheme,
  Container,
  GlobalFonts,
  PORTAL_DEFAULT_THEME,
} from '../Global'

import { sanitizeTheme } from '../../utils/sanitizeTheme'
import { version } from '../../../package.json'
import { FrameKiller } from '../FrameKiller'
import { VersionInfo } from './VersionInfo'
import FeatureFlagProvider from './FeatureFlagProvider'
import _ from 'lodash'

const messages = messagesEN

/**
 * Init Sentry
 */
const release = `${import.meta.env.VITE_SYSTEM}-${
  import.meta.env.VITE_SUBSYSTEM
}@${version}`.toLowerCase()

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  normalizeDepth: 10,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    Sentry.reportingObserverIntegration({
      types: ['crash', 'intervention', 'deprecation'],
    }),
  ],
  tracePropagationTargets: [
    new RegExp(/localhost/),
    new RegExp(/\.unfoldr\.dev/),
    new RegExp(/\.streaem\.com/),
    new RegExp(/dev-streaem\.com/),
  ],
  environment: import.meta.env.VITE_NODE_ENV,
  release,
  maxBreadcrumbs: 50,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
})

// Log application initialization
Sentry.addBreadcrumb({
  message: ':: Application initialized ::',
  level: 'info',
  data: {
    version,
    release,
    environment: import.meta.env.VITE_NODE_ENV,
    timestamp: new Date().toISOString(),
  },
})

/**
 * Init react query client
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
})

const env = import.meta.env.VITE_NODE_ENV

/**
 * Initialises global setup
 * - Router
 * - Locale/Intl
 * - Fonts
 * - Redux store
 * - React Query
 * - Service worker
 * - Sentry
 */
export const SetupApp: FC = ({ children }) => {
  const [isSafe, setIsSafe] = useState<boolean>()

  useEffect(() => {
    if (window.self === window.top) {
      setIsSafe(true)
    } else {
      setIsSafe(false)
    }
  }, [])

  if (isSafe === false) {
    return (
      <>
        <FrameKiller />
      </>
    )
  }

  if (isSafe) {
    return (
      <>
        <BrowserRouter>
          <IntlProvider
            defaultLocale="en"
            locale={navigator.language.split('-')[0] || 'en'}
            messages={messages}
          >
            <Sentry.ErrorBoundary
              fallback={<I18NText id="error.unknown" />}
              showDialog
            >
              <Provider store={store}>
                <ThemeProvider theme={sanitizeTheme(PORTAL_DEFAULT_THEME)}>
                  <FeatureFlagProvider>
                    <VersionInfo />
                    <GlobalFonts />
                    <AppTheme />
                    <QueryClientProvider client={queryClient}>
                      <Container
                        display="flex"
                        backgroundColor="rgba(0,0,0,0.015)"
                        overflowX="auto"
                      >
                        <Container flex="1">&nbsp;</Container>
                        <Container
                          minWidth="1280px"
                          width="100%"
                          maxWidth="1440px"
                          height="100vh"
                          backgroundColor="var(--background-color-default)"
                        >
                          {children}
                        </Container>
                        <Container flex="1">&nbsp;</Container>
                      </Container>
                      <Container>
                        {env === 'local' && (
                          <ReactQueryDevtools initialIsOpen />
                        )}
                      </Container>
                    </QueryClientProvider>
                  </FeatureFlagProvider>
                </ThemeProvider>
              </Provider>
            </Sentry.ErrorBoundary>
          </IntlProvider>
        </BrowserRouter>
      </>
    )
  }

  return <></>
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(console.log)

// @ts-expect-error Add store on window when running e2e tests
if (window.Cypress) window.store = store
